import type { QueryResults, System } from '../../odie';

import { Cell } from '../components/MapComponent';
import { config } from '../defs/config';
import type { BlockEntity, SpiderBlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { mapIterateCells } from '../util/mapTools';

function isSpiderBlockEntity(entity: BlockEntity): entity is SpiderBlockEntity {
  const maybeSpiderBlockEntity = entity as SpiderBlockEntity;
  return !!maybeSpiderBlockEntity.c.blockSpider;
}

/*
    render system that does a few extra things needed for this game
*/
export class RenderSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'render';

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene!: GameScene;
  public queries!: QueryResults;

  // impl
  //-------------------------------------------------------------------------
  public update(): void {
    // iterate map cells
    mapIterateCells(this.scene, (cell: Cell) => {
      const base = cell.base;
      const overlay = cell.overlay;

      // step base then overlay. ignore if child.
      if (base && !base.parent) this._stepBase(base.entity);
      if (overlay && !overlay.parent)
        this._stepOverlay(overlay.entity, base?.entity);
    });
  }

  // private: step
  //-------------------------------------------------------------------------
  private _stepBase(entity: BlockEntity) {
    // blocks above should overlap those below. so we just invert screen y.
    // then we align by tile size to simplify following calculations. these
    // should not increase z by more than <1.
    let z = -entity.view.y / config.tile.size;

    // on overlap prop, add just enough to be over neighbors (left,right,below)
    if (entity.c.block.overlap) z += 0.01;

    if (isSpiderBlockEntity(entity)) {
      // spiders should always be rendered above all lines, so they can move
      // up over preceding blocks and so their web can be drawn above the whole
      // grid, while behaving like immobile base blocks
      z += 10;
    }

    // set new z index
    entity.view.zIndex = z;
  }

  private _stepOverlay(entity: BlockEntity, base?: BlockEntity) {
    // if base exists, overlay overlaps base position, else uses same logic as base
    const z = base ? base.view.zIndex + 0.1 : -entity.view.y / config.tile.size;

    // set new z index
    entity.view.zIndex = z;
  }
}
